/**
 * @file LegendsLayer.tsx
 * @description Legend components for the map visualization.
 */

import { useMetricColorScale } from '@/hooks/useMetricColorScale';
import { useVizStore } from '@/store';

interface LegendsLayerProps {
  width: number;
  height: number;
}

export function LegendsLayer({ width, height }: LegendsLayerProps) {
  const polyColor = useVizStore((s) => s.polyColor);
  const pointsColor = useVizStore((s) => s.pointsColor);
  const voteSupportColor = useVizStore((s) => s.voteSupportColor);
  const voteAgainstColor = useVizStore((s) => s.voteAgainstColor);
  const voteAbsenceColor = useVizStore((s) => s.voteAbsenceColor);
  const useBins = useVizStore((s) => s.useBins) ?? true;

  // Determine if vote legend should be shown
  const showVoteLegend = polyColor === 'voteType' || pointsColor === 'voteType';

  // Determine if metric legend should be shown
  const showMetricLegend = polyColor === 'metric' || pointsColor === 'metric';

  // Get metric scale data with thresholds for legend display
  const metricLegendData = useMetricColorScale(showMetricLegend, true);

  return (
    <>
      {/* Vote Legend - Bottom Right */}
      {showVoteLegend && (
        <g transform={`translate(${width - 210}, ${height - 56})`}>
          <rect width={260} height={30} fill="white" opacity={0.9} rx={6} />
          <g transform="translate(10, 12)" fontSize={11} fill="#333">
            <rect
              x={0}
              y={0}
              width={14}
              height={10}
              fill={voteSupportColor || '#80CFA9'}
              stroke="#000"
              strokeWidth={0.2}
            />
            <text x={20} y={9}>
              Yea
            </text>

            <rect
              x={70}
              y={0}
              width={14}
              height={10}
              fill={voteAgainstColor || '#cccc1b'}
              stroke="#000"
              strokeWidth={0.2}
            />
            <text x={90} y={9}>
              Nay
            </text>

            <rect
              x={140}
              y={0}
              width={14}
              height={10}
              fill={voteAbsenceColor || '#DDD9E7'}
              stroke="#000"
              strokeWidth={0.2}
            />
            <text x={160} y={9}>
              No Vote
            </text>
          </g>
        </g>
      )}

      {/* Metric Legend - Bottom Left */}
      {showMetricLegend &&
        metricLegendData.scale &&
        metricLegendData.min !== null &&
        metricLegendData.max !== null && (
          <g transform={`translate(10, ${height - 56})`}>
            <rect width={200} height={30} fill="white" opacity={0.9} rx={6} />
            {useBins && metricLegendData.colors && metricLegendData.thresholds ? (
              <>
                {/* Binned color rectangles */}
                <g transform="translate(10, 10)">
                  {metricLegendData.colors.map((color, i) => {
                    const binWidth = 180 / metricLegendData.colors!.length;
                    return (
                      <rect
                        key={i}
                        x={i * binWidth}
                        y={0}
                        width={binWidth}
                        height={10}
                        fill={color}
                        stroke="#000"
                        strokeWidth={0.2}
                      />
                    );
                  })}
                </g>
                {/* Bin threshold labels */}
                <g transform="translate(10, 28)" fontSize={10} fill="#333">
                  {metricLegendData.thresholds.map((threshold, i) => {
                    const binWidth = 180 / metricLegendData.colors!.length;
                    const x = i * binWidth;
                    // Show label at start and end, and optionally in between if there's space
                    const isFirst = i === 0;
                    const isLast = i === metricLegendData.thresholds!.length - 1;
                    const showLabel = isFirst || isLast;
                    if (!showLabel) return null;
                    return (
                      <text key={i} x={x} y={0} textAnchor={isFirst ? 'start' : 'end'}>
                        {Math.round(threshold)}
                      </text>
                    );
                  })}
                </g>
              </>
            ) : (
              <>
                {/* Gradient bar */}
                <defs>
                  <linearGradient id="metric-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    {(() => {
                      // Generate color stops for the gradient
                      // TypeScript knows min and max are not null due to the outer conditional check
                      const min = metricLegendData.min;
                      const max = metricLegendData.max;
                      if (min === null || max === null) return [];
                      const stops = [];
                      const numStops = 20; // Number of color stops for smooth gradient
                      for (let i = 0; i <= numStops; i++) {
                        const t = i / numStops;
                        const value = min + (max - min) * t;
                        const color = metricLegendData.scale(value);
                        stops.push(
                          <stop key={i} offset={`${(i / numStops) * 100}%`} stopColor={color} />,
                        );
                      }
                      return stops;
                    })()}
                  </linearGradient>
                </defs>
                <rect
                  x={10}
                  y={10}
                  width={180}
                  height={10}
                  fill="url(#metric-gradient)"
                  stroke="#000"
                  strokeWidth={0.2}
                />
                {/* Min/Max labels */}
                <g transform="translate(10, 28)" fontSize={10} fill="#333">
                  <text x={0} y={0} textAnchor="start">
                    {Math.round(metricLegendData.min)}
                  </text>
                  <text x={180} y={0} textAnchor="end">
                    {Math.round(metricLegendData.max)}
                  </text>
                </g>
              </>
            )}
          </g>
        )}
    </>
  );
}
